import React, { useEffect, useState } from 'react';
import { ICON } from '../../icons/iconIds';
import IconButton from '../icons/IconButton';
import Button from '../ui/Button';
import { documentDesignService } from '../../services/documentDesignService';
import { LAYOUT_SLOTS, formatBytes, slotFitness } from './slotFitness';
import MediaDeleteModal from './MediaDeleteModal';

const CONSENT_OPTIONS = ['N/A', 'On file', 'None'];

const RATING_LABELS = {
  good: 'Print ready',
  fair: 'Acceptable',
  low: 'Too low',
  vector: 'Vector',
  unknown: 'Unknown',
};

function formFromItem(item) {
  return {
    approved_external: Boolean(item?.approved_external),
    photo_credit: item?.photo_credit || '',
    date_taken: item?.date_taken ? String(item.date_taken).slice(0, 10) : '',
    photo_location: item?.photo_location || '',
    people_consent: item?.people_consent || 'N/A',
    photo_notes: item?.photo_notes || '',
  };
}

const MediaDetailPanel = ({ item: summary, onClose, onUpdated, onDeleted }) => {
  const [item, setItem] = useState(summary);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(formFromItem(summary));
  const [userTags, setUserTags] = useState(summary?.user_tags || []);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [error, setError] = useState(null);
  const [showOverlays, setShowOverlays] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setItem(summary);
    setForm(formFromItem(summary));
    setUserTags(summary?.user_tags || []);
    setError(null);
    setConfirmDelete(false);
    setLoading(true);
    documentDesignService
      .getMedia(summary.id)
      .then((detail) => {
        if (cancelled) return;
        setItem(detail);
        setForm(formFromItem(detail));
        setUserTags(detail.user_tags || []);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [summary]);

  const dirtyForm = JSON.stringify(form) !== JSON.stringify(formFromItem(item));
  const dirtyTags = JSON.stringify([...userTags].sort()) !== JSON.stringify([...(item.user_tags || [])].sort());

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !userTags.includes(tag)) setUserTags([...userTags, tag]);
    setTagInput('');
  };

  const retryTagging = async () => {
    setTagging(true);
    setError(null);
    try {
      const updated = await documentDesignService.enrichMedia(item.id);
      setItem(updated);
      setForm(formFromItem(updated));
      setUserTags(updated.user_tags || []);
      onUpdated?.(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setTagging(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch = {};
      if (dirtyForm) Object.assign(patch, { ...form, date_taken: form.date_taken || null });
      if (dirtyTags) patch.user_tags = userTags;
      const updated = await documentDesignService.updateMedia(item.id, patch);
      setItem(updated);
      setForm(formFromItem(updated));
      setUserTags(updated.user_tags || []);
      onUpdated?.(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const preview = item.is_vector
    ? (item.original_url || item.renditions?.web?.url || item.renditions?.thumb?.url)
    : (item.renditions?.web?.url || item.renditions?.thumb?.url || item.original_url);

  return (
    <aside className="dd-detail-panel" aria-label="Photo details">
      <div className="dd-detail-header">
        <h3 title={item.file_name}>{item.file_name}</h3>
        <IconButton icon={ICON.ddClose} onClick={onClose} ariaLabel="Close details" />
      </div>

      <div className="dd-detail-preview">
        {preview ? <img src={preview} alt={item.description || item.file_name} /> : <div className="dd-empty-preview">No preview</div>}
        {showOverlays && item.focal_point && (
          <span
            className="dd-focal-point"
            style={{ left: `${item.focal_point.x * 100}%`, top: `${item.focal_point.y * 100}%` }}
            title="Focal point"
          />
        )}
        {showOverlays &&
          (item.text_safe_areas || []).map((a, i) => (
            <span
              key={i}
              className="dd-safe-area"
              style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, width: `${a.w * 100}%`, height: `${a.h * 100}%` }}
              title="Text-safe area"
            />
          ))}
      </div>
      {(item.focal_point || item.text_safe_areas?.length > 0) && (
        <label className="dd-inline-check">
          <input type="checkbox" checked={showOverlays} onChange={(e) => setShowOverlays(e.target.checked)} />
          Show focal point and text-safe areas
        </label>
      )}

      {loading && <div className="dd-detail-loading">Loading details…</div>}
      {error && <div className="dd-error-inline">{error}</div>}

      {item.ppe_concern && (
        <div className="dd-warning">
          <strong>Safety review:</strong> {item.ppe_notes || 'Possible missing PPE.'}
        </div>
      )}

      <section className="dd-detail-section">
        <h4>AI description</h4>
        {item.description ? (
          <p className="dd-description">{item.description}</p>
        ) : (
          <p className="dd-muted">
            {item.enrichment_status === 'error'
              ? `Tagging failed: ${item.enrichment_error || 'unknown error'}`
              : item.enrichment_status === 'skipped'
                ? (item.is_vector ? 'Legacy vector — run AI tagging to describe and search.' : 'Not tagged.')
                : item.is_vector && item.enrichment_status === 'pending'
                  ? 'Vector queued for AI tagging.'
                  : 'Queued for AI tagging.'}
          </p>
        )}
        <div className="dd-chip-row">
          {item.setting && <span className="dd-chip setting">{item.setting}</span>}
          {item.quality_score != null && <span className="dd-chip">Quality {Math.round(item.quality_score * 100)}%</span>}
          {item.cover_suitable && <span className="dd-chip good">Cover candidate</span>}
          {item.contains_people && <span className="dd-chip">People</span>}
        </div>
        <div className="dd-chip-row">
          {(item.tags || []).map((t) => (
            <span key={t} className="dd-chip tag">{t}</span>
          ))}
        </div>
        {(item.enrichment_status !== 'skipped' || item.is_vector) && (
          <div className="dd-detail-actions">
            <Button
              size="sm"
              variant="secondary"
              loading={tagging}
              onClick={retryTagging}
            >
              {item.enrichment_status === 'error'
                ? 'Retry AI tagging'
                : item.description
                  ? 'Re-tag with AI'
                  : 'Run AI tagging'}
            </Button>
          </div>
        )}
        {item.enrichment_model && (
          <p className="dd-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
            Model: {item.enrichment_model}
            {item.enriched_at ? ` · ${new Date(item.enriched_at).toLocaleString()}` : ''}
          </p>
        )}
      </section>

      <section className="dd-detail-section">
        <h4>Curator tags</h4>
        <div className="dd-chip-row">
          {userTags.map((t) => (
            <span key={t} className="dd-chip user-tag">
              {t}
              <button type="button" onClick={() => setUserTags(userTags.filter((x) => x !== t))} aria-label={`Remove ${t}`}>
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="dd-tag-input">
          <input
            type="text"
            value={tagInput}
            placeholder="Add tag and press Enter"
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
          />
        </div>
      </section>

      <section className="dd-detail-section">
        <h4>Print fitness</h4>
        <ul className="dd-fitness-list">
          {LAYOUT_SLOTS.map((slot) => {
            const fit = slotFitness(item, slot);
            return (
              <li key={slot.id}>
                <span>{slot.label}</span>
                <span className={`dd-fit ${fit.rating}`}>
                  {RATING_LABELS[fit.rating]}
                  {fit.dpi ? ` · ${fit.dpi} dpi` : ''}
                  {fit.cropPct > 0 ? ` · ${fit.cropPct}% crop` : ''}
                </span>
              </li>
            );
          })}
        </ul>
        {item.dominant_colors?.length > 0 && (
          <div className="dd-swatches" title="Dominant colours">
            {item.dominant_colors.map((c) => (
              <span key={c.hex} className="dd-swatch" style={{ background: c.hex, flexGrow: Math.max(c.share, 0.05) }} title={`${c.hex} · ${Math.round(c.share * 100)}%`} />
            ))}
          </div>
        )}
      </section>

      <section className="dd-detail-section">
        <h4>SharePoint metadata</h4>
        <div className="dd-form">
          <label className="dd-inline-check">
            <input
              type="checkbox"
              checked={form.approved_external}
              onChange={(e) => setField('approved_external', e.target.checked)}
            />
            Approved for external use
          </label>
          <label>
            Credit
            <input type="text" value={form.photo_credit} onChange={(e) => setField('photo_credit', e.target.value)} />
          </label>
          <label>
            Date taken
            <input type="date" value={form.date_taken} onChange={(e) => setField('date_taken', e.target.value)} />
          </label>
          <label>
            Location
            <input type="text" value={form.photo_location} onChange={(e) => setField('photo_location', e.target.value)} />
          </label>
          <label>
            People consent
            <select value={form.people_consent} onChange={(e) => setField('people_consent', e.target.value)}>
              {CONSENT_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
          <label>
            Notes
            <textarea rows={3} value={form.photo_notes} onChange={(e) => setField('photo_notes', e.target.value)} />
          </label>
        </div>
        <div className="dd-detail-actions">
          <Button onClick={save} loading={saving} disabled={!dirtyForm && !dirtyTags}>
            Save
          </Button>
          <Button variant="ghost" onClick={() => { setForm(formFromItem(item)); setUserTags(item.user_tags || []); }} disabled={!dirtyForm && !dirtyTags}>
            Reset
          </Button>
        </div>
      </section>

      <section className="dd-detail-section dd-file-facts">
        <dl>
          <dt>Folder</dt><dd>{item.folder_path || '(root)'}</dd>
          <dt>Dimensions</dt><dd>{item.width_px && item.height_px ? `${item.width_px} × ${item.height_px} px` : '—'}</dd>
          <dt>Size</dt><dd>{formatBytes(item.size_bytes)}</dd>
          <dt>Modified</dt><dd>{item.sp_modified_at ? new Date(item.sp_modified_at).toLocaleString() : '—'}{item.sp_modified_by ? ` by ${item.sp_modified_by}` : ''}</dd>
          {item.exif_taken_at && (<><dt>Camera date</dt><dd>{new Date(item.exif_taken_at).toLocaleDateString()}</dd></>)}
        </dl>
        <div className="dd-detail-links">
          {item.sharepoint_web_url && (
            <a href={item.sharepoint_web_url} target="_blank" rel="noreferrer">Open in SharePoint ↗</a>
          )}
          {item.original_url && (
            <a href={item.original_url} target="_blank" rel="noreferrer">Download original ↗</a>
          )}
        </div>
      </section>

      {onDeleted && (
        <section className="dd-detail-section dd-detail-danger">
          <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
            Delete from library and SharePoint
          </Button>
        </section>
      )}

      {confirmDelete && (
        <MediaDeleteModal
          item={item}
          onClose={() => setConfirmDelete(false)}
          onDeleted={(id, result) => {
            setConfirmDelete(false);
            onDeleted(id, result);
          }}
        />
      )}
    </aside>
  );
};

export default MediaDetailPanel;
